<template>
  <v-card
    class="incident-card"
    :class="{
      'critical-border': incident.priority === 'CRITICAL',
      'high-border': incident.priority === 'HIGH',
      'medium-border': incident.priority === 'MEDIUM',
      'low-border': incident.priority === 'LOW'
    }"
    elevation="2"
    @click="$emit('view', incident)"
  >
    <!-- Header de la tarjeta -->
    <v-card-title class="card-header pa-3">
      <div class="d-flex align-center">
        <v-icon
          :color="getPriorityColor(incident.priority)"
          :icon="getPriorityIcon(incident.priority)"
          size="20"
          class="mr-2"
        ></v-icon>
        <div class="flex-grow-1">
          <div class="incident-title text-body-1 font-weight-medium">
            {{ incident.title }}
          </div>
          <div class="incident-id text-caption text-grey">
            #{{ incident.id }}
          </div>
        </div>
        <v-chip
          :color="getStatusColor(incident.status)"
          size="x-small"
          variant="flat"
        >
          {{ getStatusText(incident.status) }}
        </v-chip>
      </div>
    </v-card-title>

    <!-- Contenido de la tarjeta -->
    <v-card-text class="pa-3 pt-0">
      <!-- Descripción -->
      <p class="incident-description text-body-2 mb-3">
        {{ truncateDescription(incident.description) }}
      </p>

      <!-- Información de la agencia -->
      <div class="incident-agency d-flex align-center mb-2">
        <v-icon icon="mdi-store" size="16" class="mr-2 text-grey"></v-icon>
        <span class="text-body-2">{{ incident.agencyName }}</span>
      </div>

      <!-- Supervisor asignado -->
      <div class="incident-supervisor d-flex align-center mb-2">
        <v-icon icon="mdi-account" size="16" class="mr-2 text-grey"></v-icon>
        <div v-if="incident.assignedTo" class="d-flex align-center">
          <v-avatar size="20" class="mr-2">
            <v-img :src="incident.assignedTo.avatar || '/default-avatar.png'"></v-img>
          </v-avatar>
          <span class="text-body-2">{{ incident.assignedTo.name }}</span>
        </div>
        <span v-else class="text-body-2 text-grey">Sin asignar</span>
      </div>

      <!-- Categoría -->
      <div class="incident-category mb-3">
        <v-chip
          :color="getCategoryColor(incident.category)"
          size="small"
          variant="outlined"
        >
          {{ getCategoryText(incident.category) }}
        </v-chip>
      </div>

      <!-- Fecha y tiempo -->
      <div class="incident-time d-flex align-center text-caption text-grey">
        <v-icon icon="mdi-clock-outline" size="14" class="mr-1"></v-icon>
        <span>{{ formatDate(incident.createdAt) }}</span>
        <v-spacer></v-spacer>
        <span>{{ getTimeAgo(incident.createdAt) }}</span>
      </div>
    </v-card-text>

    <!-- Acciones de la tarjeta -->
    <v-card-actions class="pa-2">
      <v-btn
        icon="mdi-eye"
        size="small"
        variant="text"
        @click.stop="$emit('view', incident)"
        title="Ver detalles"
      ></v-btn>
      
      <!-- W2 LIFECYCLE-SIBLING: generic edit is not a sold first-pilot capability. -->
      <v-btn
        v-if="canUseLifecycleWrites"
        icon="mdi-pencil"
        size="small"
        variant="text"
        @click.stop="$emit('edit', incident)"
        title="Editar"
      ></v-btn>
      
      <!-- P13-V7B (refutation): assignment is offered only where its producer admits the session
           (eligible-assignees / assign-user answer department_head alone); the page decides and passes it. -->
      <v-btn
        v-if="canAssign"
        icon="mdi-account-arrow-right"
        size="small"
        variant="text"
        @click.stop="$emit('assign', incident)"
        title="Asignar"
      ></v-btn>
      
      <v-btn
        icon="mdi-comment-plus"
        size="small"
        variant="text"
        @click.stop="$emit('comment', incident)"
        title="Comentar"
      ></v-btn>
      
      <v-spacer></v-spacer>
      
      <!-- W2 LIFECYCLE-SIBLING: generic delete is not a sold first-pilot capability. -->
      <v-btn
        v-if="canUseLifecycleWrites"
        icon="mdi-delete"
        size="small"
        variant="text"
        color="error"
        @click.stop="$emit('delete', incident)"
        title="Eliminar"
      ></v-btn>
    </v-card-actions>

    <!-- Indicador de prioridad -->
    <div
      class="priority-indicator"
      :class="`priority-${String(incident.priority || 'none').toLowerCase()}`"
    ></div>
  </v-card>
</template>

<script setup lang="ts">
import type { Incident } from '@/types/incidents'

interface Props {
  incident: Incident
  /**
   * W2 LIFECYCLE-SIBLING client truth. The card renders its OWN edit/delete buttons, so hiding the
   * parent's row actions would not have hidden these. The predicate is passed down rather than
   * recomputed here so the whole client keeps ONE mirror of the merged backend allowlist. Defaults
   * to `false`: a caller that forgets to pass it shows nothing, which is the safe direction.
   */
  canUseLifecycleWrites?: boolean
  /**
   * P13-V7B (refutation): the card drew "Asignar" for every role while the page — rightly — ignored
   * the click for anyone its producer refuses: a dead control. Passed down like the lifecycle
   * predicate; defaults to `false`. (The priority indicator also no longer throws on a row with no
   * priority.)
   */
  canAssign?: boolean
}

withDefaults(defineProps<Props>(), { canUseLifecycleWrites: false, canAssign: false })

defineEmits<{
  view: [incident: Incident]
  edit: [incident: Incident]
  delete: [incident: Incident]
  assign: [incident: Incident]
  comment: [incident: Incident]
}>()

// Métodos auxiliares
const getPriorityColor = (priority: string) => {
  const colors = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityIcon = (priority: string) => {
  const icons = {
    CRITICAL: 'mdi-alert-circle',
    HIGH: 'mdi-alert',
    MEDIUM: 'mdi-information',
    LOW: 'mdi-check-circle'
  }
  return icons[priority] || 'mdi-help'
}

const getStatusColor = (status: string) => {
  const colors = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status: string) => {
  if (!status) return ''
  
  // Normalizar a UPPERCASE para coincidir con las claves del diccionario
  const normalizedStatus = status.toString().toUpperCase()
  
  const texts = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    PENDING_VALIDATION: 'Pendiente Validación',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }
  
  return texts[normalizedStatus] || status
}

const getCategoryColor = (category: string) => {
  const colors = {
    PERSONAL: 'purple',
    TECHNICAL: 'blue',
    INFRASTRUCTURE: 'orange',
    ELECTRICAL: 'yellow',
    INTERNET: 'green',
    EQUIPMENT: 'teal',
    OTHER: 'grey'
  }
  return colors[category] || 'grey'
}

const getCategoryText = (category: string) => {
  const texts = {
    PERSONAL: 'Personal',
    TECHNICAL: 'Técnica',
    INFRASTRUCTURE: 'Infraestructura',
    ELECTRICAL: 'Eléctrica',
    INTERNET: 'Internet',
    EQUIPMENT: 'Equipos',
    OTHER: 'Otra'
  }
  return texts[category] || category
}

const truncateDescription = (description: string, maxLength = 100) => {
  if (description.length <= maxLength) return description
  return description.substring(0, maxLength) + '...'
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('es-DO', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTimeAgo = (date: string) => {
  const now = new Date()
  const past = new Date(date)
  const diffMs = now.getTime() - past.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffDays > 0) {
    return `hace ${diffDays}d`
  } else if (diffHours > 0) {
    return `hace ${diffHours}h`
  } else {
    return 'hace poco'
  }
}
</script>

<style scoped>
.incident-card {
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.incident-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Bordes de prioridad */
.critical-border {
  border-left-color: #f44336;
}

.high-border {
  border-left-color: #ff9800;
}

.medium-border {
  border-left-color: #2196f3;
}

.low-border {
  border-left-color: #4caf50;
}

/* Header de la tarjeta */
.card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.incident-title {
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.incident-id {
  font-weight: 500;
}

/* Contenido */
.incident-description {
  line-height: 1.4;
  color: #424242;
}

.incident-agency,
.incident-supervisor {
  min-height: 24px;
}

/* Indicador de prioridad */
.priority-indicator {
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 20px 20px 0;
}

.priority-critical {
  border-color: transparent #f44336 transparent transparent;
}

.priority-high {
  border-color: transparent #ff9800 transparent transparent;
}

.priority-medium {
  border-color: transparent #2196f3 transparent transparent;
}

.priority-low {
  border-color: transparent #4caf50 transparent transparent;
}

/* Responsive */
@media (max-width: 768px) {
  .incident-card {
    margin-bottom: 1rem;
  }
  
  .incident-title {
    font-size: 0.9rem;
  }
  
  .incident-description {
    font-size: 0.8rem;
  }
}

/* Estados específicos */
.incident-card[data-status="PENDING_VALIDATION"] {
  opacity: 0.8;
}

.incident-card[data-status="CLOSED"] {
  opacity: 0.6;
}

.incident-card[data-status="CANCELLED"] {
  opacity: 0.5;
  filter: grayscale(20%);
}

/* Animaciones */
.v-chip {
  transition: all 0.2s ease;
}

.v-btn {
  transition: all 0.2s ease;
}

.incident-card .v-card-actions .v-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
